// Axios interceptor for session token management and single login enforcement
import axios from 'axios';
import { resetSession } from './sessionReset';

// setupAxiosInterceptors() used to only run from AppContent's useEffect in
// App.js -- but on mount, React fires a CHILD's effects before its parent's,
// so on a hard reload straight into a route whose own useEffect fires API
// calls immediately (e.g. NextGenDashboard's dashboard/projects/github
// fetches), those very first requests went out before this had registered
// at all: no x-builder-id header attached, and neither the retry-once
// logic nor the session-expired-modal dispatch below got a chance to run
// either (that in-flight request's promise chain was built without them).
// The raw backend 400 ("Builder session required") fell straight through
// to the page's own bare error state instead of the proper "please log in
// again" experience this file already builds.
//
// Fix: register the interceptors exactly once, synchronously, the moment
// this module is imported (see the self-invocation at the bottom of this
// file) -- that happens as part of App.js's module evaluation, before React
// even starts rendering, so no request can ever go out ahead of it.
// _navigateRef lets the later setupAxiosInterceptors(navigate) call from
// AppContent's useEffect just upgrade which navigate() function the 401
// handler uses, without re-registering (and thus double-firing) the
// interceptors themselves.
let _interceptorsRegistered = false;
let _navigateRef = null;

/**
 * Setup axios interceptors for session token management
 */
export const setupAxiosInterceptors = (navigate) => {
  if (navigate) {
    _navigateRef = navigate;
  }
  if (_interceptorsRegistered) {
    return;
  }
  _interceptorsRegistered = true;

  // Request interceptor: Add session token to all requests
  axios.interceptors.request.use(
    (config) => {
      // Never attach session headers to external APIs (e.g. api.github.com).
      // Those servers don't allow our custom headers in CORS preflight.
      const url = config.url || '';
      const isExternal = url.startsWith('http') &&
        !url.includes('theskillgenie.com') &&
        !url.includes('localhost');
      if (isExternal) {
        // Also strip any session headers that were pre-set as axios defaults
        delete config.headers.Authorization;
        delete config.headers['X-Session-Token'];
        return config;
      }

      // Attach the NextGen Builder identity header for every
      // /api/nextgen-builder/* request. The backend's nextgenAuthMiddleware
      // requires an `x-builder-id` (or `user-id`) header on nearly every
      // NextGen Builder route (dashboard, plans, payments, wallet, profile,
      // referral, projects, ...), but nothing was ever setting it as a
      // default axios header or attaching it per-request — every plain
      // `axios.get(...)` call from the NextGen Builder pages went out with
      // no identity header at all and was rejected with 400 "Builder
      // session required". This is what caused the empty plans grid on
      // /nextgen-builder/purchase. Centralizing it here (instead of every
      // individual page) fixes all of them at once.
      if (url.includes('/api/nextgen-builder/') && !config.headers['x-builder-id']) {
        try {
          const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
          // Every call to this API prefix originates from a NextGen Builder
          // page, so the stored `user` object IS the builder — deliberately
          // NOT gated on `accountType` here. A session created (logged in)
          // before that field was added to the backend login response would
          // never carry it, so requiring it would silently keep sending no
          // header at all for any already-logged-in builder — the exact
          // reason the first version of this fix still 400'd for existing
          // sessions.
          if (storedUser && storedUser.id != null) {
            config.headers['x-builder-id'] = storedUser.id;
          }
        } catch (e) {
          // Ignore malformed localStorage state — request goes out without
          // the header, same as before this fix.
        }
      }

      // Get session token from localStorage
      const sessionToken = localStorage.getItem('session_token');

      if (sessionToken) {
        // Add session token to Authorization header
        config.headers.Authorization = `Bearer ${sessionToken}`;
        // For student tenant flow, avoid sending X-Session-Token to prevent CORS preflight issues
        const currentUser = JSON.parse(localStorage.getItem('user') || "{}");
        const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const isStudentTenant = currentUser && (currentUser.role === 'student' && (currentUser.tenant || currentUser.college_id || !isMainPlatform));
        if (!isStudentTenant) {
          // Also add as X-Session-Token header for compatibility when safe
          config.headers['X-Session-Token'] = sessionToken;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Handle session invalidation
  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const { response, config } = error;

      // NextGen Builder's own x-builder-id-header auth (nextgenAuthMiddleware)
      // is a completely separate scheme from the JWT/session_token handling
      // below — a 400 with this exact message means the header was missing,
      // not that a JWT expired. Before treating it as a real expired
      // session, retry ONCE with the header attached explicitly from
      // localStorage: the request interceptor above can register a beat
      // after some page's first request already fired on a hard reload, so
      // that request goes out with no header at all even though the
      // builder IS still logged in (see the long comment in
      // NextGenBuilder/hooks/useNextGenSubscription.js — this used to wipe
      // a perfectly valid session and bounce the builder off the page).
      // Only fall through to clearing the session + showing the modal if
      // the retry also fails, or there is no stored builder to retry with.
      if (
        response?.status === 400 &&
        response?.data?.message === 'Builder session required' &&
        config &&
        (config.url || '').includes('/api/nextgen-builder/')
      ) {
        if (!config._builderSessionRetried) {
          try {
            const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
            if (storedUser && storedUser.id != null) {
              config._builderSessionRetried = true;
              config.headers = config.headers || {};
              config.headers['x-builder-id'] = storedUser.id;
              return axios(config);
            }
          } catch (e) {
            // Malformed localStorage — fall through to session-expired handling.
          }
        }

        // Genuine expiry (or nothing to retry with): clear the NextGen
        // Builder identity and let NextGenLayout's SessionExpiredModal
        // (listening for this event) show a proper "please log in again"
        // popup, instead of every page rendering its own raw backend
        // error message inline.
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        localStorage.removeItem('user_id');
        localStorage.removeItem('user_pk');
        localStorage.removeItem('auth_token');
        delete axios.defaults.headers.common['x-builder-id'];
        window.dispatchEvent(new CustomEvent('nextgen-builder:session-expired'));
        return Promise.reject(error);
      }

      // Check if session is invalid (401 with SESSION_INVALID code)
      if (response?.status === 401) {
        const errorData = response.data;
        const errorCode = errorData?.code;
        
        // Handle session invalidation (another device logged in)
        if (errorCode === 'SESSION_INVALID' || errorCode === 'SESSION_REQUIRED') {
          // Clear all session data
          localStorage.removeItem('session_token');
          localStorage.removeItem('user');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_pk');
          localStorage.removeItem('auth_token');
          
          // Clear axios default headers
          delete axios.defaults.headers.common.Authorization;
          delete axios.defaults.headers.common['X-Session-Token'];
          
          // Show message to user
          const message = errorData?.error || 
            'Your session has been invalidated. Another device may have logged in. Please log in again.';
          
          // Use window.location for navigation if navigate is not available
          if (_navigateRef) {
            _navigateRef('/login', { 
              state: { 
                error: message,
                sessionInvalidated: true 
              } 
            });
          } else {
            // Fallback: use window.location
            alert(message);
            window.location.href = '/login';
          }
        }
      }
      
      return Promise.reject(error);
    }
  );
};

// Registered immediately on import (see the header comment above).
// AppContent's useEffect(() => setupAxiosInterceptors(navigate), [navigate])
// in App.js still runs later on every render where navigate changes -- it
// just supplies the real navigate() function via _navigateRef at that
// point; the interceptors themselves are already live by then.
setupAxiosInterceptors();

/**
 * Initialize axios interceptors on app startup
 * Call this in your App.jsx or main entry point
 */
export const initAxiosInterceptors = () => {
  // Get session token and set default headers if it exists
  const sessionToken = localStorage.getItem('session_token');
  if (sessionToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${sessionToken}`;
    const currentUser = JSON.parse(localStorage.getItem('user') || "{}");
    const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isStudentTenant = currentUser && (currentUser.role === 'student' && (currentUser.tenant || currentUser.college_id || !isMainPlatform));
    if (!isStudentTenant) {
      axios.defaults.headers.common['X-Session-Token'] = sessionToken;
    }
  }
};

/**
 * Clear session and logout user
 * Call this when user explicitly logs out
 */
export const clearSession = async () => {
  // Kept as an export because other modules import it, but the implementation
  // now lives in ONE place. This version cleared five keys and two headers;
  // sessionReset clears the full user-scoped inventory, bumps the session
  // epoch so in-flight responses are discarded, and broadcasts so components
  // holding private state in memory reset themselves.
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { return {}; }
  })();
  await resetSession({ kind: user.role === 'college' ? 'college' : 'user' });
};




